import { css, html, LitElement, nothing, TemplateResult } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { BackendPlan, getPlanRate, LOB, METALLIC_LEVEL, metallicSortOrder } from '../../util/plans';

import '../../components/plan-card';
import '../../components/compare-bar';
import '../../components/agent-banner';
import '../../components/subsidy-banner';
import '../../components/sort-select';
import { getIshopForm, ISHOP_FORM_ID, RateQuoteFormData } from '../../util/form';
import { consume, provide } from '@lit/context';
import { inputResponseContext } from '../../contexts/inputResponse';
import {
    BackendFormResponse,
    Producer,
    submitBlueAdvantageSelection,
    submitSaveQuote,
    AGENT_PORTAL_DASH_URL,
} from '../../util/api';
import { EVENT, makeCustomEvent } from '../../util/events';
import { breakpointContext } from '../../contexts/breakpoint';
import { BREAKPOINT } from '../../util/breakpoints';
import { classMap } from 'lit/directives/class-map.js';
import './filter-card';
import { Filters, MARKETPLACE, SORT } from '../../util/filter-sort';
import { filtersContext } from '../../contexts/filters';
import { sortContext } from '../../contexts/sort';
import { SOURCE, isEmbeddedAgentEmailDelivery } from '../../util/source';
import { sourceContext } from '../../contexts/source';
import { deliveryMethodContext } from '../../contexts/deliveryMethod';
import { agentContext } from '../../contexts/agent';
import { getAnalyticPageDescription, getHostURL } from '../../util/analytics';
import { STORAGE_KEYS } from '../../util/storage';
import { ACA_HEALTH_INSURANCE_2026 } from '../../enums/textConstantEnums';
import { PAGE } from '../../util/page';
import { readOnlyContext } from '../../contexts/readOnly';

@customElement('rq-marketplace-results')
export class MarketPlaceResults extends LitElement {
    static styles = css`
        .wrapper {
            width: 100%;
            display: flex;
            flex-direction: column;
        }
        .content {
            padding-top: 32px;
            display: flex;
            flex-direction: row;
            gap: 32px;
            padding-bottom: 60px;
        }
        .sort {
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            gap: 32px;
            width: 100%;
        }
        .sort-select {
            width: 312px;
            margin-top: auto;
        }
        .tabs {
            width: 70%;
            border-bottom: 1px solid #d1d3d4;
        }
        .filter {
            flex: 1;
        }
        .mobile-filter {
            position: absolute;
            top: var(--wrapper-top, 64px);
            left: 0;
            bottom: 0;
            width: 100%;
            height: 100%;
            z-index: 200;
            background-color: white;
            padding: 24px;
            box-sizing: border-box;
        }
        .filter-btn,
        .save-btn {
            margin-bottom: 32px;
        }
        .plans {
            width: 70%;
        }
        .mobile-plans {
            width: 100%;
        }
        .plan-cards {
            display: flex;
            flex-direction: column;
            gap: 32px;
        }
        .tab-description,
        .sort-select {
            padding: 32px 0px;
            display: flex;
            flex-direction: column;
            gap: 24px;
        }
        .estimate-disclaimer {
            display: flex;
            flex-direction: column;
            gap: 8px;
        }
        .capitalize {
            text-transform: capitalize;
        }

        .savings-header,
        .subsidy-savings {
            display: flex;
            flex-direction: row;
            gap: 16px;
            align-items: center;
        }
        .subsidy-savings {
            gap: 8px;
        }
        .hide {
            display: none;
        }
        .quote-saved-wrapper {
            min-height: 100vh;
            width: 100%;
            display: flex;
            align-items: flex-start;
            justify-content: center;
            padding-top: 25vh;
            box-sizing: border-box;
        }
    `;

    constructor() {
        super();
        this.resizeHandler = this.resizeHandler.bind(this);
        this.compareHandler = this.compareHandler.bind(this);
    }

    async connectedCallback() {
        super.connectedCallback();
        this.addEventListener(EVENT.COMPARE_SELECTION, this.compareHandler as EventListener);
        this.addEventListener(EVENT.SET_FILTER_SORT, this.filterSetHandler as EventListener);
        this.addEventListener(EVENT.CLOSE_FILTERS, this.onCloseFilters);
        window.addEventListener(EVENT.COMPARE_BAR_REMOVAL, this.compareHandler as EventListener);
        addEventListener('resize', this.resizeHandler);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener(EVENT.COMPARE_SELECTION, this.compareHandler as EventListener);
        this.removeEventListener(EVENT.SET_FILTER_SORT, this.filterSetHandler as EventListener);
        this.removeEventListener(EVENT.CLOSE_FILTERS, this.onCloseFilters);
        window.removeEventListener(EVENT.COMPARE_BAR_REMOVAL, this.compareHandler as EventListener);
        removeEventListener('resize', this.resizeHandler);
    }

    firstUpdated(): void {
        this.setFilterHeight();
        const storageFilters = sessionStorage.getItem(STORAGE_KEYS.FILTERS);
        if (storageFilters) {
            const parsed = JSON.parse(storageFilters) as Filters;
            // Always recalculate marketplace — stale session value can filter all plans when subsidy eligibility changes
            parsed.marketplace = this.inputResponse?.subsidy?.subsidyEligible ? MARKETPLACE.ON : MARKETPLACE.OFF;
            this.filters = parsed;
        } else {
            let filters: Filters;

            filters = {
                ...this.filters,
                marketplace: this.inputResponse?.subsidy?.subsidyEligible ? MARKETPLACE.ON : MARKETPLACE.OFF,
            };
            if (this.plans.length) {
                filters = {
                    ...filters,
                    // TODO: fix with top pick
                    // topPick: Boolean(this.inputResponse?.plans.some((p) => p.pickOrder && p.pickOrder >= 0)),
                };
            }
            this.filters = { ...filters, marketplace: filters?.marketplace as MARKETPLACE | null };
            // TODO: fix with top pick
            // this.filters.topPick = this.isTopPick === false || this.isTopPick === 'false' ? false : true;
        }

        window.bcncDataLayer = window.bcncDataLayer || {};
        window.bcncDataLayer.page = window.bcncDataLayer.page || {};
        // TODO: fix with top pick
        // window.bcncDataLayer.page.view_url = `${getHostURL()}/results/${this.filters.topPick ? 'top' : 'all'}`;
        window.bcncDataLayer.page.view_url = `${getHostURL()}/results/all}`;
    }

    protected updated(changedProperties: Map<string, unknown>): void {
        super.updated(changedProperties);
        if (this.source === SOURCE.EMBEDDED_AGENT && changedProperties.has('inputResponse')) {
            const marketplace = this.inputResponse?.subsidy?.subsidyEligible ? MARKETPLACE.ON : MARKETPLACE.OFF;
            const filters = {
                ...this.filters,
                marketplace,
            };
            this.filters = { ...filters };
        }
        if (changedProperties.has('filters')) {
            sessionStorage.setItem(STORAGE_KEYS.FILTERS, JSON.stringify(this.filters));
        }
    }

    private onCloseFilters() {
        window.bcncDataLayer = window.bcncDataLayer || {};
        window.bcncDataLayer.page = window.bcncDataLayer.page || {};
        // TODO: fix with top pick
        // window.bcncDataLayer.page.view_url = `${getHostURL()}/results/${this.filters.topPick ? 'top' : 'all'}`;
        window.bcncDataLayer.page.view_url = `${getHostURL()}/results/all`;
        this.filterOpen = false;
    }

    private resizeHandler() {
        this.setFilterHeight();
    }

    @consume({ context: inputResponseContext, subscribe: true })
    @state()
    inputResponse?: BackendFormResponse;

    @consume({ context: breakpointContext, subscribe: true })
    breakpoint?: BREAKPOINT;

    @consume({ context: sourceContext, subscribe: true })
    source?: SOURCE;

    @consume({ context: deliveryMethodContext, subscribe: true })
    deliveryMethod?: string;

    @consume({ context: agentContext, subscribe: true })
    agent?: Producer;

    @query('rq-filter-sort')
    filterSortDiv!: HTMLElement;

    get plans() {
        return this.inputResponse?.plans || [];
    }

    @provide({ context: filtersContext })
    @state()
    filters: Filters = {
        marketplace: null,
        productType: [],
        metallicLevel: [],
        easyPricing: false,
        // topPick: true,
    };

    @provide({ context: sortContext })
    @state()
    sort: SORT = SORT.LOW_TO_HIGH;

    @state()
    filterOpen = false;

    @state()
    private quoteSaved = false;

    @property({ type: Object })
    submittedForm!: RateQuoteFormData;

    @property({ type: Array })
    comparePlans: BackendPlan[] = [];

    @query('.wrapper')
    wrapperDiv!: HTMLDivElement;

    @property({ type: Boolean })
    isTopPick: boolean | string = true;

    @consume({ context: readOnlyContext, subscribe: true })
    @state()
    readOnly = false;

    get isShopper() {
        return this.inputResponse?.context?.toLowerCase() === 'shopper';
    }

    private setFilterHeight() {
        if (!this.wrapperDiv) return;
        this.style.setProperty('--wrapper-top', `${this.wrapperDiv.getBoundingClientRect().y + 4}px`);
    }

    private filterSetHandler({ detail: { filters, sort } }: CustomEvent<{ filters: Filters; sort: SORT }>) {
        if (filters) {
            this.filters = filters;
        }
        if (sort) {
            this.sort = sort;
        }
    }

    private compareHandler(e: CustomEvent) {
        if (!e.detail.planCode) return;
        if (this.comparePlans.some((p) => p.planCode === e.detail.planCode)) {
            const comparePlans = [...this.comparePlans].filter((p) => p.planCode !== e.detail.planCode);
            this.comparePlans = comparePlans;
        } else if (this.comparePlans.length < 3) {
            const plan = this.plans.find((p) => p.planCode === e.detail.planCode);
            if (!plan) return;
            const comparePlans = [...this.comparePlans];
            comparePlans.push(plan);
            this.comparePlans = comparePlans;
        }
    }

    get plansToShow(): BackendPlan[] {
        let plans = [...this.plans];
        if (this.filters.productType.length) {
            plans = plans.filter((p) => this.filters.productType.includes(p.lob as LOB));
        }
        if (this.filters.metallicLevel.length) {
            plans = plans.filter((p) => this.filters.metallicLevel.includes(p.metal as METALLIC_LEVEL));
        }
        if (this.filters.marketplace !== null) {
            plans = plans.filter((p) => p.marketplace === this.filters.marketplace);
        }
        if (this.filters.easyPricing) {
            plans = plans.filter((p) => this.filters.easyPricing && p?.pricing.toLowerCase() === 'easy');
        }
        // TODO: fix with top pick
        // if (this.filters.topPick) {
        //     plans = plans.filter((p) => p.pickOrder && p.pickOrder >= 0);
        // }
        // return plans
        //     .sort((a, b) => {
        //         const aPickOrder =
        //             a.pickOrder === -1 || a.pickOrder === undefined ? Number.MAX_SAFE_INTEGER : a.pickOrder;
        //         const bPickOrder =
        //             b.pickOrder === -1 || b.pickOrder === undefined ? Number.MAX_SAFE_INTEGER : b.pickOrder;
        //         return aPickOrder - bPickOrder;
        //     })
        //     .sort((a, b) => {
        //         if (this.sort === SORT.LOW_TO_HIGH) {
        //             return a.totalIndividualResponsibilityAmt - b.totalIndividualResponsibilityAmt;
        //         } else if (this.sort === SORT.HIGH_TO_LOW) {
        //             return b.totalIndividualResponsibilityAmt - a.totalIndividualResponsibilityAmt;
        //         } else if (this.sort === SORT.METALLIC) {
        //             const aMetal = metallicSortOrder.indexOf(a.metal as METALLIC_LEVEL);
        //             const bMetal = metallicSortOrder.indexOf(b.metal as METALLIC_LEVEL);
        //             return aMetal - bMetal;
        //         } else if (this.sort === SORT.NETWORK) {
        //             // Order alphabetically by header while selecting the network sort in results page
        //             return a.header.localeCompare(b.header);
        //         }
        //         return 0;
        //     })
        //     .sort((a) => (this.inputResponse?.currentPlan === a.planCode ? -1 : 1))
        //     .sort((a, b) => (b?.producerRecommended === true ? 1 : 0) - (a?.producerRecommended === true ? 1 : 0));
        return plans.sort((a, b) => {
            // Priority 1: producerRecommended plans first
            const aRecommended = a?.producerRecommended === true ? 1 : 0;
            const bRecommended = b?.producerRecommended === true ? 1 : 0;
            if (bRecommended !== aRecommended) {
                return bRecommended - aRecommended;
            }

            // Priority 2: currentPlan second
            const aIsCurrent = this.inputResponse?.currentPlan === a.planCode ? 1 : 0;
            const bIsCurrent = this.inputResponse?.currentPlan === b.planCode ? 1 : 0;
            if (bIsCurrent !== aIsCurrent) {
                return bIsCurrent - aIsCurrent;
            }

            // Priority 3: agentRecommendedPlan
            const aAgentRecommended = a?.agentRecommendedPlan === true ? 1 : 0;
            const bAgentRecommended = b?.agentRecommendedPlan === true ? 1 : 0;
            if (bAgentRecommended !== aAgentRecommended) {
                return bAgentRecommended - aAgentRecommended;
            }

            // Priority 4: bcbsncRecommendedPlan
            const aBcbsncRecommended = a?.bcbsncRecommendedPlan === true ? 1 : 0;
            const bBcbsncRecommended = b?.bcbsncRecommendedPlan === true ? 1 : 0;
            if (bBcbsncRecommended !== aBcbsncRecommended) {
                return bBcbsncRecommended - aBcbsncRecommended;
            }

            // Priority 5: planPopularityIndex (most popular first)
            const aPopular = a?.planPopularityIndex > -1 ? 1 : 0;
            const bPopular = b?.planPopularityIndex > -1 ? 1 : 0;
            if (bPopular !== aPopular) {
                return bPopular - aPopular;
            }

            const aRate = getPlanRate(a);
            const bRate = getPlanRate(b);
            // Priority 6: Apply selected sort criteria
            if (this.sort === SORT.LOW_TO_HIGH) {
                return aRate - bRate;
            } else if (this.sort === SORT.HIGH_TO_LOW) {
                return bRate - aRate;
            } else if (this.sort === SORT.METALLIC) {
                const aMetal = metallicSortOrder.indexOf(a.metal as METALLIC_LEVEL);
                const bMetal = metallicSortOrder.indexOf(b.metal as METALLIC_LEVEL);
                return aMetal - bMetal;
            } else if (this.sort === SORT.NETWORK) {
                return a.header.localeCompare(b.header);
            }
            return 0;
        });
    }

    get tabDescription() {
        const is2026 = this.plans.some((p) => p.productYear === '2026');
        // TODO: fix with top pick
        // if (this.filters.topPick) {
        //     const severity = this.inputResponse?.plansSeverity;
        //     return html` <div class="tab-description">
        //         <lh-typography>
        //             Based on what you've told us, these ${this.inputResponse?.effectiveStart?.split('/').pop()} plans
        //             may fit your ${this.isShopper ? nothing : html`client's`}
        //             ${severity ? html`<strong>${severity}</strong> ` : nothing} coverage needs.
        //         </lh-typography>
        //         <lh-typography
        //             >Rates quoted are estimates. Actual cost will be calculated based on
        //             ${this.isShopper
        //             ? html`your plan’s effective date.`
        //             : html`the effective date of your client's plan.`}</lh-typography
        //         >
        //     </div>`;
        // }
        return html`
            <div class="tab-description">
                <div>
                    <lh-typography type="text" size="lg">
                        <strong
                            >All Plans - ${this.filters.marketplace === MARKETPLACE.ON ? 'On' : 'Off'}
                            Marketplace</strong
                        >
                    </lh-typography>
                </div>
                <div class="estimate-disclaimer">
                    <div>
                        <lh-typography>
                            Rates quoted are ${this.inputResponse?.effectiveStart?.split('/').pop()} plan year
                            estimates. Actual cost will be calculated based on
                            your${this.isShopper ? nothing : html` client's`} plan's effective date.
                        </lh-typography>
                    </div>
                    ${is2026
                        ? html`<div>
                              <lh-button variant="text" target="_blank" href=${ACA_HEALTH_INSURANCE_2026}>
                                  What impacts the cost of premiums?
                              </lh-button>
                          </div>`
                        : nothing}
                </div>
            </div>
        `;
    }

    // TODO: fix with top pick
    // private tabHandler(topPick: boolean) {
    //     window.bcncDataLayer = window.bcncDataLayer || {};
    //     window.bcncDataLayer.page = window.bcncDataLayer.page || {};
    //     window.bcncDataLayer.page.view_url = `${getHostURL()}/results/${topPick ? 'top' : 'all'}`;
    //     this.filters = {
    //         ...this.filters,
    //         topPick,
    //     };
    // }

    get agentBanner() {
        if (!this.agent) return nothing;
        return html`<rq-agent-banner .agent=${this.agent}></rq-agent-banner>`;
    }

    get compareBar() {
        if (!this.comparePlans.length) return nothing;
        return html`<rq-compare-bar .plans=${this.comparePlans}></rq-compare-bar>`;
    }

    get filtersDiv() {
        if (this.isMobile && !this.filterOpen) return nothing;
        return html`<div class=${classMap({ filter: !this.isMobile, 'mobile-filter': this.isMobile })}>
            <rq-filter-sort></rq-filter-sort>
        </div>`;
    }

    get isMobile() {
        return this.breakpoint === BREAKPOINT.PHONE || this.breakpoint === BREAKPOINT.TABLET;
    }

    get saveQuoteButton() {
        if (![SOURCE.PABOL, SOURCE.EMBEDDED_AGENT].includes(this.source!)) return nothing;
        // Embedded agent choosing to email the client acts like SOURCE.EMAIL - show sendPlanButton instead.
        if (isEmbeddedAgentEmailDelivery(this.source, this.deliveryMethod)) return nothing;
        return html`<div class="save-btn">
            <lh-button
                variant="outlined"
                icon-position="left"
                icon="tray"
                @click=${() => {
                    if (this.source === SOURCE.EMBEDDED_AGENT) {
                        dispatchEvent(makeCustomEvent(EVENT.EMBEDDED_AGENT_SAVE_QUOTE, { data: this.inputResponse }));
                        return;
                    }
                    const saveQuoteIshopForm = getIshopForm(ISHOP_FORM_ID.SAVE_QUOTE);
                    if (saveQuoteIshopForm) {
                        saveQuoteIshopForm.submit();
                        return;
                    }
                    // Standalone SPA route (e.g. /get-a-quote/... via psale=y) has no legacy form - fall back to the API.
                    submitSaveQuote()
                        .then(() => {
                            this.quoteSaved = true;
                            // Let the container shrink/hide the footer so this screen stays centered.
                            this.dispatchEvent(makeCustomEvent(EVENT.QUOTE_SAVED, { quoteSaved: true }));
                        })
                        .catch((error) => {
                            console.error('Failed to save quote: ', error);
                        });
                }}
                >Save This Quote</lh-button
            >
        </div>`;
    }

    get sendPlanButton() {
        const isEmbeddedAgentEmail = isEmbeddedAgentEmailDelivery(this.source, this.deliveryMethod);
        if (this.source !== SOURCE.EMAIL && this.source !== SOURCE.SALESFORCE && !isEmbeddedAgentEmail) return nothing;
        return html`<div class="save-btn">
            <lh-button
                variant="outlined"
                icon-position="left"
                icon="paper-plane-tilt"
                @click=${() => {
                    if (isEmbeddedAgentEmail) {
                        // Embedded agent flow doesn't use the legacy Blue Advantage selection API -
                        // just move to the producer email entry page.
                        this.dispatchEvent(
                            makeCustomEvent(EVENT.CHANGE_PAGE, {
                                page: PAGE.PRODUCER_EMAIL_ENTRY,
                            })
                        );
                        return;
                    }
                    this.submitBlueAdvantageSelection();
                }}
            >
                Send Selected Plans to My Client
            </lh-button>
        </div>`;
    }

    private async submitBlueAdvantageSelection() {
        try {
            const response = await submitBlueAdvantageSelection(this.plans);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // After successful submission, navigate to email entry page
            this.dispatchEvent(
                makeCustomEvent(EVENT.CHANGE_PAGE, {
                    page: PAGE.PRODUCER_EMAIL_ENTRY,
                })
            );
        } catch (error) {
            console.error('Failed to submit Blue Advantage selection:', error);
            // Optionally show error to user
            // You may want to dispatch an error event or show a toast notification
        }
    }

    /**
     * Came from original when suggesting this plan
     * scope.producerFunctions.sendRenewalPlan = function() {
                        $window.location = '/sapps/ratequote/producer/RenewalChangePlanForm.do?quoteYear=2014' +
                                            '&internalMemberId=' + getParameterByName('memberNumber') +
                                            '&planName=' + scope.suggestedPlan.planCode +
                                            '&deductible=' + scope.suggestedPlan.deductible +
                                            '&coinsurance=' + scope.suggestedPlan.coinsurance +
                                            '&qhpId=' + scope.suggestedPlan.qhpId +
                                            '&rate=' + scope.suggestedPlan.totalRate;
                    };
     */

    private getParameterByName(name: string) {
        name = name.replace(/[[]/, '\\[').replace(/[\]]/, '\\]');
        const params = new URLSearchParams(window.location.search);
        const results = params.get(name);
        return results == null ? '' : decodeURIComponent(results.replace(/\+/g, ' '));
    }

    get suggestPlanButton() {
        if (this.source !== SOURCE.AGENT) return nothing;
        const recommendedPlan = this.plans.find((p) => p.producerRecommended);
        return html`<div class="save-btn">
            <lh-button
                variant="outlined"
                disabled=${Boolean(!recommendedPlan || this.readOnly) || nothing}
                @click=${() => {
                    if (!recommendedPlan || this.readOnly) return;
                    window.location.href = `/sapps/ratequote/producer/RenewalChangePlanForm.do?quoteYear=2014&internalMemberId=${this.getParameterByName(
                        'memberNumber'
                    )}&planName=${recommendedPlan.planCode}&deductible=${recommendedPlan.deductible}&coinsurance=${recommendedPlan.coinsurance}&qhpId=${recommendedPlan.qhpId}&rate=${recommendedPlan.totalRate}`;
                }}
            >
                Suggest This Plan
            </lh-button>
        </div>`;
    }

    // TODO: fix with top pick
    // get viewAllPlansButton() {
    //     if (!this.isMobile || !this.filters.topPick) return nothing;
    //     return html`<lh-button
    //         analytics-id="rate-quote:${this.source}:u65:plans-${getAnalyticPageDescription(
    //         this.filters
    //     )}:view-all-plans:button-outlined"
    //         full-width
    //         icon="plus"
    //         icon-position="left"
    //         @click=${() => this.tabHandler(false)}
    //         >View All Plans</lh-button
    //     >`;
    // }

    get planCards() {
        const els: TemplateResult[] = [];
        let currentSort: string | undefined;
        this.plansToShow.forEach((p) => {
            if (this.inputResponse?.currentPlan === p.planCode || p.producerRecommended) {
                els.push(html`<rq-plan-card .comparePlans=${this.comparePlans} .plan=${p}></rq-plan-card>`);
                return;
            }
            let newSort: string | undefined;
            if (this.sort === SORT.METALLIC) {
                newSort = p.metal;
            } else if (this.sort === SORT.NETWORK) {
                newSort = p.header;
                // newSort = lobNetworkLabel
                //     .get(p.lob as LOB)
                //     ?.split('-')[0]
                //     .trim();
            }
            if (currentSort !== newSort) {
                els.push(
                    html`<div><lh-typography selector="h6" type="heading" size="lg">${newSort}</lh-typography></div>`
                );
                currentSort = newSort;
            }
            els.push(html`<rq-plan-card .comparePlans=${this.comparePlans} .plan=${p}></rq-plan-card>`);
        });
        return els;
    }

    get subsidyBanner() {
        // const responseContext = this.inputResponse?.context?.toUpperCase() ?? '';
        // const referralSource = this.inputResponse?.referralSource?.toUpperCase() ?? '';
        // const isProducerFlow =
        //     responseContext === 'PRODUCER' &&
        //     (referralSource === 'PRODUCER_ENTERED' || referralSource === 'QUOTED_SALE');

        if (this.source && [SOURCE.CHANGE_APP, SOURCE.AGENT, SOURCE.EMBEDDED_AGENT].includes(this.source))
            return nothing;
        return html`<rq-subsidy-banner></rq-subsidy-banner>`;
    }

    // TODO: fix with top pick
    // get tabsDiv() {
    //     return html`<div class="tabs">
    //         <lh-tab-group default-panel=${this.filters.topPick ? 'top' : 'all'}>
    //             <lh-tab
    //                 slot="nav"
    //                 panel="top"
    //                 analytics-id="rate-quote:${this.source}:u65:plans-${getAnalyticPageDescription(
    //         this.filters
    //     )}:top-picks:tab"
    //                 @click=${() => this.tabHandler(true)}
    //                 @keyup=${() => this.tabHandler(true)}
    //             >
    //                 Top picks
    //                 (${this.plans.filter(
    //         (p) => p.marketplace === this.filters.marketplace && p.pickOrder && p.pickOrder > 0
    //     ).length})
    //             </lh-tab>
    //             <lh-tab
    //                 slot="nav"
    //                 panel="all"
    //                 analytics-id="rate-quote:${this.source}:u65:plans-${getAnalyticPageDescription(
    //         this.filters
    //     )}:all-plans:tab"
    //                 @click=${() => this.tabHandler(false)}
    //                 @keyup=${() => this.tabHandler(false)}
    //             >
    //                 All plans (${this.plans.filter((p) => p.marketplace === this.filters.marketplace).length})
    //             </lh-tab>
    //         </lh-tab-group>
    //     </div>`;
    // }

    get quoteSavedView() {
        return html`
            <div class="quote-saved-wrapper">
                <lh-container theme="white" content-width="fluid" gutters="none" spacing="tight">
                    <lh-container alignment="center" gutters="lg" spacing="xs" context-id="rate-quote-saved">
                        <lh-typography variant="h6">Rate Quote Saved.</lh-typography>
                        <lh-button-group alignment="center" margin-top="lg">
                            <lh-button
                                variant="filled"
                                href=${AGENT_PORTAL_DASH_URL()}
                                analytics-id="rate-quote:${this.source}:u65:back-to-producer-home:button-filled"
                                >BACK TO PRODUCER HOME</lh-button
                            >
                        </lh-button-group>
                    </lh-container>
                </lh-container>
            </div>
        `;
    }

    render() {
        if (this.quoteSaved) {
            return this.quoteSavedView;
        }
        return html`
            <div class="wrapper">
                ${this.agentBanner} ${this.subsidyBanner}
                <lh-container content-width="xxl" spacing="none" gutters="md">
                    <div class="content">
                        ${this.filtersDiv}
                        <div class=${classMap({ plans: !this.isMobile, 'mobile-plans': this.isMobile })}>
                            <div class="sort">
                                ${this.tabDescription}
                                ${this.isMobile
                                    ? nothing
                                    : html`<div class="sort-select"><rq-sort-select></rq-sort-select></div>`}
                            </div>

                            ${this.isMobile
                                ? html`<div class="filter-btn">
                                      <lh-button
                                          analytics-id="rate-quote:${this
                                              .source}:u65:plans-${getAnalyticPageDescription(
                                              this.filters
                                          )}:filter-sort:button-outlined"
                                          @click=${() => {
                                              if (!this.filterOpen) {
                                                  window.bcncDataLayer = window.bcncDataLayer || {};
                                                  window.bcncDataLayer.page = window.bcncDataLayer.page || {};
                                                  window.bcncDataLayer.page.view_url = `${getHostURL()}/results/filter-sort`;
                                              }
                                              this.filterOpen = !this.filterOpen;
                                          }}
                                          icon="sliders"
                                          icon-position="right"
                                          variant="outlined"
                                          full-width
                                      >
                                          Filter + Sort
                                      </lh-button>
                                  </div>`
                                : nothing}
                            ${this.saveQuoteButton} ${this.sendPlanButton} ${this.suggestPlanButton}
                            <div class=${classMap({ 'plan-cards': true, hide: this.isMobile && this.filterOpen })}>
                                ${this.plansToShow.length
                                    ? html`${this.planCards}
                                          <!-- this.viewAllPlansButton  is currently commented out TODO: fix later -->
                                          <div>
                                              <lh-typography size="sm">
                                                  ${(() => {
                                                      const effectiveYear = this.inputResponse?.effectiveStart
                                                          ?.split('/')
                                                          ?.pop();
                                                      const surveyYear = effectiveYear === '2026' ? '2025' : '2026';
                                                      return `Plan quality ratings and enrollee survey results are calculated by CMS using data provided by health plans in ${surveyYear}. The ratings are being displayed for plans for the ${effectiveYear} plan year. Learn more about these ratings.`;
                                                  })()}
                                                  <a href="https://www.healthcare.gov/quality-ratings/" target="_blank"
                                                      >https://www.healthcare.gov/quality-ratings/<lh-icon
                                                          size="sm"
                                                          name="arrow-square-out"
                                                          class="pdfColor"
                                                      ></lh-icon
                                                  ></a>
                                              </lh-typography>
                                          </div>`
                                    : html`<lh-typography selector="h6" type="heading" size="lg"
                                          >No plans found</lh-typography
                                      >`}
                            </div>
                        </div>
                    </div>
                </lh-container>
            </div>
            ${this.compareBar}
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'rq-marketplace-results': MarketPlaceResults;
    }
}
